import {
    feedbackDialogTitleYes,
    feedbackDialogTitleNo,
    feedbackDialogSubTitle,
    donationContentToolTip,
    feedbackDialogSubmitButtonText,
    feedbackDialogCancelButtonText,
} from './InlineFeedbackDialog.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { Checkbox } from 'office-ui-fabric-react/lib/Checkbox';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { TooltipHost } from 'office-ui-fabric-react/lib/Tooltip';
import { Dialog, DialogFooter, DialogType } from 'office-ui-fabric-react/lib/Dialog';
import { PrimaryButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { setShowInlineFeedbackDialog } from '../mutators/setShowInlineFeedbackDialog';
import { setInlineFeedbackResponseCategories } from '../mutators/setInlineFeedbackResponseCategories';
import { setInlineFeedbackResponseText } from '../mutators/setInlineFeedbackResponseText';
import { setIsContentDonated } from '../mutators/setIsContentDonated';
import { setShowThankyouDialog } from '../mutators/setShowThankyouDialog';
import { resetInlineFeedbackDialogState } from '../mutators/resetInlineFeedbackDialogState';
import { logInlineFeedbackDialogResponse } from '../actions/logInlineFeedbackDialogResponse';
import { inlineFeedbackDialogStore } from '../store/store';
import { observer } from 'mobx-react';
import InlineFeedbackDialogThankYou from './InlineFeedbackDialogThankYou';
import { ControlIcons } from 'owa-control-icons';
import {
    shouldShowContentDonationText,
    sliceCategories,
    sliceCategory,
} from '../utils/validateInlineFeedbackDialogProps';
import { lazyPostSuggestionAndSendLogDatapoint } from 'owa-uservoice';
import UserVoiceFeedbackType from 'owa-service/lib/contract/UserVoiceFeedbackType';
import { UserVoiceInlineFeedbackTags } from 'owa-uservoice';
import { YES, NOCATEGORYSELECTED } from '../FeedbackConstants';
import sendFeedbackToOds from '../utils/sendFeedbackToOds';

let styles = require('./InlineFeedbackDialog.scss');
let inlineFeedbackDialogDiv: HTMLDivElement;

interface InlineFeedbackDialogProps {
    feedbackDialogParams: FeedbackDialogParams;
    sessionId: string;
    ribbonResponse: string;
}

export interface FeedbackCategory {
    logString: string;
    uiString: string;
}

export interface FeedbackDialogParams {
    title?: string;
    subTitle?: string;
    categories?: FeedbackCategory[];
    ghostText?: string;
    donationText?: string;
    donationToolTip?: string;
    donationJson?: Object;
    submitCallBack?: (
        inlineFeedbackResponseText: string,
        inlineFeedbackResponseCategories: string[]
    ) => void;
    cancelCallBack?: () => void;
    createUVTicket?: boolean;
}

@observer
class InlineFeedbackDialog extends React.Component<InlineFeedbackDialogProps, {}> {
    constructor(props: any) {
        super(props);
        setShowInlineFeedbackDialog(true);
    }

    componentWillUnmount() {
        resetInlineFeedbackDialogState();
    }

    private onDismissed() {
        if (inlineFeedbackDialogDiv) {
            ReactDOM.unmountComponentAtNode(inlineFeedbackDialogDiv);
            document.body.removeChild(inlineFeedbackDialogDiv);
        }
    }

    private closeDialog() {
        setShowInlineFeedbackDialog(false);
    }

    render() {
        return (
            <Dialog
                hidden={!inlineFeedbackDialogStore().showInlineFeedbackDialog}
                onDismiss={this.closeDialog}
                dialogContentProps={{
                    showCloseButton: true,
                    type: DialogType.close,
                    title: inlineFeedbackDialogStore().showThankyouDialog
                        ? ''
                        : this.props.feedbackDialogParams.title ||
                          (this.props.ribbonResponse === YES
                              ? loc(feedbackDialogTitleYes)
                              : loc(feedbackDialogTitleNo)),
                }}
                modalProps={{
                    isBlocking: true,
                    containerClassName: styles.inlineFeedbackDialog,
                    onDismissed: this.onDismissed,
                }}>
                {inlineFeedbackDialogStore().showThankyouDialog ? (
                    <InlineFeedbackDialogThankYou onCloseButtonClick={this.closeDialog} />
                ) : (
                    <div>
                        <div className={styles.inlineFeedbackSubTitle}>
                            {this.props.feedbackDialogParams.subTitle ||
                                loc(feedbackDialogSubTitle)}
                        </div>
                        {this.props.feedbackDialogParams.categories && (
                            <div>{this.createCategoryCheckboxes()}</div>
                        )}
                        {this.props.feedbackDialogParams.ghostText && (
                            <div>
                                <TextField
                                    className={
                                        this.props.feedbackDialogParams.categories &&
                                        this.props.feedbackDialogParams.categories.length > 0
                                            ? styles.smallTextField
                                            : styles.largeTextField
                                    }
                                    multiline={true}
                                    placeholder={this.props.feedbackDialogParams.ghostText}
                                    onChange={this.onTextChange}
                                />
                            </div>
                        )}
                        {shouldShowContentDonationText(this.props.feedbackDialogParams) && (
                            <div className={styles.donationDiv}>
                                <Checkbox
                                    label={this.props.feedbackDialogParams.donationText}
                                    name={this.props.feedbackDialogParams.donationText}
                                    defaultChecked={false}
                                    onChange={this.onDonationCheckboxChange}
                                />
                                <div className={styles.infoIconDiv}>
                                    <TooltipHost
                                        content={
                                            this.props.feedbackDialogParams.donationToolTip ||
                                            loc(donationContentToolTip)
                                        }>
                                        <Icon
                                            iconName={ControlIcons.Info}
                                            className={styles.infoIcon}
                                        />
                                    </TooltipHost>
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <PrimaryButton
                                onClick={this.onSubmit}
                                className={styles.submitButton}
                                disabled={this.isSubmitDisabled()}>
                                {loc(feedbackDialogSubmitButtonText)}
                            </PrimaryButton>
                            <DefaultButton className={styles.cancelButton} onClick={this.onCancel}>
                                {loc(feedbackDialogCancelButtonText)}
                            </DefaultButton>
                        </DialogFooter>
                    </div>
                )}
            </Dialog>
        );
    }

    private isSubmitDisabled(): boolean {
        if (
            inlineFeedbackDialogStore().inlineFeedbackResponseText ||
            inlineFeedbackDialogStore().inlineFeedbackResponseCategories.length > 0
        ) {
            return false;
        }

        return true;
    }

    private onSubmit = () => {
        // Saving a local copy of these store properties to assure that these are getting logged after the
        // subsequent async calls complete. This is to avoid a race condition wherein the store was being reset
        // before the async call to ODS completes.
        let inlineFeedbackResponseText: string = inlineFeedbackDialogStore()
            .inlineFeedbackResponseText;
        let inlineFeedbackResponseCategories: string[] =
            inlineFeedbackDialogStore().inlineFeedbackResponseCategories.length > 0
                ? inlineFeedbackDialogStore().inlineFeedbackResponseCategories
                : [NOCATEGORYSELECTED];
        let isContentDonated: boolean = inlineFeedbackDialogStore().isContentDonated;

        let userVoiceInlineFeedbackTags: UserVoiceInlineFeedbackTags = {
            Feature_Feedback_Custom_Data: inlineFeedbackResponseCategories.join(' | '),
        };

        if (this.props.feedbackDialogParams.createUVTicket) {
            lazyPostSuggestionAndSendLogDatapoint
                .import()
                .then(postSuggestionAndSendLogDatapoint =>
                    postSuggestionAndSendLogDatapoint(
                        'New Ticket from Feature Feedback',
                        inlineFeedbackResponseText,
                        UserVoiceFeedbackType.FeatureFeedback,
                        '',
                        userVoiceInlineFeedbackTags
                    )
                );
        }

        // If the content is being donated to ODS, logInlineFeedbackDialogResponse will be called
        // after the async call to ODS is complete. This makes sure that the Diagnostic Session Id
        // (which is sent back from ODS) is being logged to the cosmos stream.
        if (isContentDonated) {
            sendFeedbackToOds(
                this.props.feedbackDialogParams.donationJson,
                inlineFeedbackResponseText,
                inlineFeedbackResponseCategories
            ).then(response => {
                logInlineFeedbackDialogResponse({
                    sessionId: this.props.sessionId,
                    inlineFeedbackResponseText: inlineFeedbackResponseText,
                    inlineFeedbackResponseCategories: inlineFeedbackResponseCategories,
                    isContentDonated: isContentDonated,
                    isUVTicketCreated: this.props.feedbackDialogParams.createUVTicket,
                    odsResponse: response,
                });
            });
        } else {
            logInlineFeedbackDialogResponse({
                sessionId: this.props.sessionId,
                inlineFeedbackResponseText: inlineFeedbackResponseText,
                inlineFeedbackResponseCategories: inlineFeedbackResponseCategories,
                isContentDonated: isContentDonated,
                isUVTicketCreated: this.props.feedbackDialogParams.createUVTicket,
                odsResponse: '',
            });
        }

        if (this.props.feedbackDialogParams.submitCallBack) {
            this.props.feedbackDialogParams.submitCallBack(
                inlineFeedbackResponseText,
                inlineFeedbackResponseCategories
            );
        }

        setShowThankyouDialog(true);
    };

    private onCancel = () => {
        if (this.props.feedbackDialogParams.cancelCallBack) {
            this.props.feedbackDialogParams.cancelCallBack();
        }

        this.closeDialog();
    };

    private createCategoryCheckboxes = () => {
        let slicedCategories: FeedbackCategory[] = sliceCategories(
            this.props.feedbackDialogParams.categories
        );
        return slicedCategories.map(this.createCategoryCheckbox);
    };

    private createCategoryCheckbox = (category: FeedbackCategory) => {
        let slicedCategory = sliceCategory(category.uiString);

        if (slicedCategory === category.uiString) {
            return (
                <Checkbox
                    className={styles.checkbox}
                    key={category.logString || slicedCategory}
                    label={slicedCategory}
                    name={category.logString || slicedCategory}
                    defaultChecked={false}
                    onChange={this.onCategoryCheckboxChange}
                />
            );
        } else {
            return (
                <TooltipHost content={category.uiString}>
                    <Checkbox
                        className={styles.checkbox}
                        key={category.logString || slicedCategory}
                        label={slicedCategory}
                        name={category.logString || slicedCategory}
                        defaultChecked={false}
                        onChange={this.onCategoryCheckboxChange}
                    />
                </TooltipHost>
            );
        }
    };

    private onTextChange(
        ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        text: string
    ) {
        setInlineFeedbackResponseText(text);
    }

    private onDonationCheckboxChange(
        ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        checked?: boolean
    ) {
        setIsContentDonated(checked);
    }

    private onCategoryCheckboxChange(
        ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        checked?: boolean
    ) {
        setInlineFeedbackResponseCategories(ev.currentTarget.getAttribute('name'), checked);
    }
}

export default function showInlineFeedbackDialog(
    feedbackDialogParams: FeedbackDialogParams,
    sessionId: string,
    ribbonResponse: string
) {
    inlineFeedbackDialogDiv = document.createElement('div');
    document.body.appendChild(inlineFeedbackDialogDiv);

    ReactDOM.render(
        <InlineFeedbackDialog
            feedbackDialogParams={feedbackDialogParams}
            sessionId={sessionId}
            ribbonResponse={ribbonResponse}
        />,
        inlineFeedbackDialogDiv
    );
}
